import isEqual from 'lodash/isEqual';
import {
	type Action,
	createActionsHook,
	createContainer,
	createHook,
	createStateHook,
	createStore,
} from 'react-sweet-state';

import type { AggError } from '@atlassian/search-client';

import type { SelectFilterOption } from '../../../common/ui/filters/select-filter';
import type { QuickFindContent } from '../../quick-find/fetch-results/types';

export type SelectedQueryFilter = {
	filterId: string;
	keyword: string;
	displayText: string;
	option: SelectFilterOption;
};

type CurrentQueryFilter = {
	filterId: string;
	keyword: string;
};

export type QuickFindState = {
	quickFindOpen: boolean;
	quickFindQuery: string;
	content?: QuickFindContent;
	isFeedbackModalOpen: boolean;
	isBulkConnectModalOpen: boolean;
	fetchEntitiesRequestId?: string;
	fetchEntitiesTraceId?: string;
	fetchSpacesRequestId?: string;
	retryCount: number;
	updateRecentQueries?: () => void;
	updateZeroQueries?: () => void;
	selectedQueryFilters: SelectedQueryFilter[];
	currentQueryFilter?: CurrentQueryFilter;
	openHoverCardId: string | null;
	searchErrors?: AggError[];
};

export const initialState: {
    quickFindOpen: false;
    quickFindQuery: string;
    isFeedbackModalOpen: false;
    isBulkConnectModalOpen: false;
    retryCount: number;
    updateRecentQueries: undefined;
    updateZeroQueries: undefined;
    selectedQueryFilters: never[];
    openHoverCardId: null;
    searchErrors: never[];
} = {
	quickFindOpen: false,
	quickFindQuery: '',
	isFeedbackModalOpen: false,
	isBulkConnectModalOpen: false,
	retryCount: 0,
	updateRecentQueries: undefined,
	updateZeroQueries: undefined,
	selectedQueryFilters: [],
	openHoverCardId: null,
	searchErrors: [],
} satisfies QuickFindState;

const actions = {
	setQuickFindOpen:
		(quickFindOpen: QuickFindState['quickFindOpen']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ quickFindOpen });
		},
	setQuickFindQuery:
		(quickFindQuery: QuickFindState['quickFindQuery']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ quickFindQuery });
		},
	setContent:
		(content: QuickFindContent): Action<QuickFindState> =>
		({ setState }) => {
			setState({ content });
		},
	setIsBulkConnectModalOpen:
		(isBulkConnectModalOpen: QuickFindState['isBulkConnectModalOpen']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ isBulkConnectModalOpen });
		},
	setIsFeedbackModalOpen:
		(isFeedbackModalOpen: QuickFindState['isFeedbackModalOpen']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ isFeedbackModalOpen });
		},
	setRetryCount:
		(retryCount: QuickFindState['retryCount']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ retryCount });
		},
	setFetchSpacesRequestId:
		(fetchSpacesRequestId: QuickFindState['fetchSpacesRequestId']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ fetchSpacesRequestId });
		},
	setFetchEntitiesRequestId:
		(fetchEntitiesRequestId: QuickFindState['fetchEntitiesRequestId']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ fetchEntitiesRequestId });
		},
	setFetchEntitiesTraceId:
		(fetchEntitiesTraceId: QuickFindState['fetchEntitiesTraceId']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ fetchEntitiesTraceId });
		},
	setSearchErrors:
		(searchErrors: QuickFindState['searchErrors']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ searchErrors });
		},
	setUpdateRecentQueries:
		(updateRecentQueries: QuickFindState['updateRecentQueries']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ updateRecentQueries });
		},
	setUpdateZeroQueries:
		(updateZeroQueries: QuickFindState['updateZeroQueries']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ updateZeroQueries });
		},
	addSelectedQueryFilter:
		(queryFilter: SelectedQueryFilter): Action<QuickFindState> =>
		({ setState, getState }) => {
			const { selectedQueryFilters } = getState();
			const duplicate = selectedQueryFilters.find((currentQueryFilter) =>
				isEqual(JSON.stringify(currentQueryFilter), JSON.stringify(queryFilter)),
			);

			if (!duplicate) {
				setState({ selectedQueryFilters: [...selectedQueryFilters, queryFilter] });
			}
		},
	deleteSelectedQueryFilter:
		(queryFilter: SelectedQueryFilter): Action<QuickFindState> =>
		({ setState, getState }) => {
			const { selectedQueryFilters } = getState();

			const newSelectedQueryFilters = selectedQueryFilters.filter(
				(currentQueryFilter) =>
					!isEqual(JSON.stringify(currentQueryFilter), JSON.stringify(queryFilter)),
			);

			setState({ selectedQueryFilters: newSelectedQueryFilters });
		},
	resetSelectedQueryFilters:
		(): Action<QuickFindState> =>
		({ setState }) => {
			setState({ selectedQueryFilters: [] });
		},
	setCurrentQueryFilter:
		(currentQueryFilter: QuickFindState['currentQueryFilter']): Action<QuickFindState> =>
		({ setState }) => {
			setState({ currentQueryFilter });
		},
	setHoverCardOpen:
		(itemId: string, isOpen: boolean): Action<QuickFindState> =>
		({ setState, getState }) => {
			const { openHoverCardId } = getState();

			if (isOpen) {
				setState({ openHoverCardId: itemId });
			} else {
				if (openHoverCardId === itemId) {
					setState({ openHoverCardId: null });
				}
			}
		},
};

type TActions = typeof actions;

const Store = createStore<QuickFindState, TActions>({
	initialState,
	actions,
	name: 'quick-find',
});

export const useQuickFindActions: import("react-sweet-state").HookActionsFunction<import("react-sweet-state").BoundActions<QuickFindState, {
    setQuickFindOpen: (quickFindOpen: QuickFindState["quickFindOpen"]) => Action<QuickFindState>;
    setQuickFindQuery: (quickFindQuery: QuickFindState["quickFindQuery"]) => Action<QuickFindState>;
    setContent: (content: QuickFindContent) => Action<QuickFindState>;
    setIsBulkConnectModalOpen: (isBulkConnectModalOpen: QuickFindState["isBulkConnectModalOpen"]) => Action<QuickFindState>;
    setIsFeedbackModalOpen: (isFeedbackModalOpen: QuickFindState["isFeedbackModalOpen"]) => Action<QuickFindState>;
    setRetryCount: (retryCount: QuickFindState["retryCount"]) => Action<QuickFindState>;
    setFetchSpacesRequestId: (fetchSpacesRequestId: QuickFindState["fetchSpacesRequestId"]) => Action<QuickFindState>;
    setFetchEntitiesRequestId: (fetchEntitiesRequestId: QuickFindState["fetchEntitiesRequestId"]) => Action<QuickFindState>;
    setFetchEntitiesTraceId: (fetchEntitiesTraceId: QuickFindState["fetchEntitiesTraceId"]) => Action<QuickFindState>;
    setSearchErrors: (searchErrors: QuickFindState["searchErrors"]) => Action<QuickFindState>;
    setUpdateRecentQueries: (updateRecentQueries: QuickFindState["updateRecentQueries"]) => Action<QuickFindState>;
    setUpdateZeroQueries: (updateZeroQueries: QuickFindState["updateZeroQueries"]) => Action<QuickFindState>;
    addSelectedQueryFilter: (queryFilter: SelectedQueryFilter) => Action<QuickFindState>;
    deleteSelectedQueryFilter: (queryFilter: SelectedQueryFilter) => Action<QuickFindState>;
    resetSelectedQueryFilters: () => Action<QuickFindState>;
    setCurrentQueryFilter: (currentQueryFilter: QuickFindState["currentQueryFilter"]) => Action<QuickFindState>;
    setHoverCardOpen: (itemId: string, isOpen: boolean) => Action<QuickFindState>;
}>> = createActionsHook(Store);

export const useQuickFindOpen: import("react-sweet-state").HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state) => state.quickFindOpen,
});

export const useQuickFindQuery: import("react-sweet-state").HookStateFunction<string, void> = createStateHook(Store, {
	selector: (state) => state.quickFindQuery,
});

export const useContent: import("react-sweet-state").HookStateFunction<QuickFindContent, void> = createStateHook(Store, {
	selector: (state) => state.content,
});

export const useIsFeedbackModalOpen: import("react-sweet-state").HookFunction<boolean, import("react-sweet-state").BoundActions<QuickFindState, {
    setQuickFindOpen: (quickFindOpen: QuickFindState["quickFindOpen"]) => Action<QuickFindState>;
    setQuickFindQuery: (quickFindQuery: QuickFindState["quickFindQuery"]) => Action<QuickFindState>;
    setContent: (content: QuickFindContent) => Action<QuickFindState>;
    setIsBulkConnectModalOpen: (isBulkConnectModalOpen: QuickFindState["isBulkConnectModalOpen"]) => Action<QuickFindState>;
    setIsFeedbackModalOpen: (isFeedbackModalOpen: QuickFindState["isFeedbackModalOpen"]) => Action<QuickFindState>;
    setRetryCount: (retryCount: QuickFindState["retryCount"]) => Action<QuickFindState>;
    setFetchSpacesRequestId: (fetchSpacesRequestId: QuickFindState["fetchSpacesRequestId"]) => Action<QuickFindState>;
    setFetchEntitiesRequestId: (fetchEntitiesRequestId: QuickFindState["fetchEntitiesRequestId"]) => Action<QuickFindState>;
    setFetchEntitiesTraceId: (fetchEntitiesTraceId: QuickFindState["fetchEntitiesTraceId"]) => Action<QuickFindState>;
    setSearchErrors: (searchErrors: QuickFindState["searchErrors"]) => Action<QuickFindState>;
    setUpdateRecentQueries: (updateRecentQueries: QuickFindState["updateRecentQueries"]) => Action<QuickFindState>;
    setUpdateZeroQueries: (updateZeroQueries: QuickFindState["updateZeroQueries"]) => Action<QuickFindState>;
    addSelectedQueryFilter: (queryFilter: SelectedQueryFilter) => Action<QuickFindState>;
    deleteSelectedQueryFilter: (queryFilter: SelectedQueryFilter) => Action<QuickFindState>;
    resetSelectedQueryFilters: () => Action<QuickFindState>;
    setCurrentQueryFilter: (currentQueryFilter: QuickFindState["currentQueryFilter"]) => Action<QuickFindState>;
    setHoverCardOpen: (itemId: string, isOpen: boolean) => Action<QuickFindState>;
}>, void> = createHook(Store, {
	selector: (state) => state.isFeedbackModalOpen,
});

export const useIsBulkConnectModalOpen: import("react-sweet-state").HookFunction<boolean, import("react-sweet-state").BoundActions<QuickFindState, {
    setQuickFindOpen: (quickFindOpen: QuickFindState["quickFindOpen"]) => Action<QuickFindState>;
    setQuickFindQuery: (quickFindQuery: QuickFindState["quickFindQuery"]) => Action<QuickFindState>;
    setContent: (content: QuickFindContent) => Action<QuickFindState>;
    setIsBulkConnectModalOpen: (isBulkConnectModalOpen: QuickFindState["isBulkConnectModalOpen"]) => Action<QuickFindState>;
    setIsFeedbackModalOpen: (isFeedbackModalOpen: QuickFindState["isFeedbackModalOpen"]) => Action<QuickFindState>;
    setRetryCount: (retryCount: QuickFindState["retryCount"]) => Action<QuickFindState>;
    setFetchSpacesRequestId: (fetchSpacesRequestId: QuickFindState["fetchSpacesRequestId"]) => Action<QuickFindState>;
    setFetchEntitiesRequestId: (fetchEntitiesRequestId: QuickFindState["fetchEntitiesRequestId"]) => Action<QuickFindState>;
    setFetchEntitiesTraceId: (fetchEntitiesTraceId: QuickFindState["fetchEntitiesTraceId"]) => Action<QuickFindState>;
    setSearchErrors: (searchErrors: QuickFindState["searchErrors"]) => Action<QuickFindState>;
    setUpdateRecentQueries: (updateRecentQueries: QuickFindState["updateRecentQueries"]) => Action<QuickFindState>;
    setUpdateZeroQueries: (updateZeroQueries: QuickFindState["updateZeroQueries"]) => Action<QuickFindState>;
    addSelectedQueryFilter: (queryFilter: SelectedQueryFilter) => Action<QuickFindState>;
    deleteSelectedQueryFilter: (queryFilter: SelectedQueryFilter) => Action<QuickFindState>;
    resetSelectedQueryFilters: () => Action<QuickFindState>;
    setCurrentQueryFilter: (currentQueryFilter: QuickFindState["currentQueryFilter"]) => Action<QuickFindState>;
    setHoverCardOpen: (itemId: string, isOpen: boolean) => Action<QuickFindState>;
}>, void> = createHook(Store, {
	selector: (state) => state.isBulkConnectModalOpen,
});

export const useRetryCount: import("react-sweet-state").HookStateFunction<number, void> = createStateHook(Store, { selector: (state) => state.retryCount });

export const useFetchSpacesRequestId: import("react-sweet-state").HookStateFunction<string | undefined, void> = createStateHook(Store, {
	selector: (state) => state.fetchSpacesRequestId,
});

export const useFetchEntitiesRequestId: import("react-sweet-state").HookStateFunction<string | undefined, void> = createStateHook(Store, {
	selector: (state) => state.fetchEntitiesRequestId,
});

export const useFetchEntitiesTraceId: import("react-sweet-state").HookStateFunction<string | undefined, void> = createStateHook(Store, {
	selector: (state) => state.fetchEntitiesTraceId,
});

export const useUpdateRecentQueries: import("react-sweet-state").HookStateFunction<(() => void) | undefined, void> = createStateHook(Store, {
	selector: (state) => state.updateRecentQueries,
});

export const useUpdateZeroQueries: import("react-sweet-state").HookStateFunction<(() => void) | undefined, void> = createStateHook(Store, {
	selector: (state) => state.updateZeroQueries,
});

export const useSelectedQueryFilters: import("react-sweet-state").HookStateFunction<SelectedQueryFilter[], void> = createStateHook(Store, {
	selector: (state) => state.selectedQueryFilters,
});

export const useCurrentQueryFilter: import("react-sweet-state").HookStateFunction<CurrentQueryFilter | undefined, void> = createStateHook(Store, {
	selector: (state) => state.currentQueryFilter,
});

export const useSearchErrors: import("react-sweet-state").HookStateFunction<AggError[], void> = createStateHook(Store, {
	selector: (state) => state.searchErrors || [],
});

/**
 * This store is contained within a container as we may need to support multiple
 * instances of the Quick Find component in Nav3. This would be the case if we had
 * one in the navigation bar and another one in the search page.
 */
export const QuickFindStoreContainer: import("react-sweet-state").OverrideContainerComponent<unknown> = createContainer(Store);
